import { Dungeon } from '../dungeon/Dungeon';
import { CellType } from '../types';

export interface Geometry {
  vertices: Float32Array;
  vertexCount: number;
}

const WALL_HEIGHT = 1.0;

// Simple hash for deterministic random texture selection per wall face
function hashCell(x: number, z: number, direction: number): number {
  const hash = (x * 73856093) ^ (z * 19349663) ^ (direction * 83492791);
  return Math.abs(hash);
}

// Second hash for UV transform selection (different prime multipliers)
function hashCellTransform(x: number, z: number, direction: number): number {
  const hash = (x * 48611) ^ (z * 96137) ^ (direction * 15731);
  return Math.abs(hash);
}

// Encode texture index (0-3) and UV transform (0-7) into a single float
// texIndex encodes: textureId + transformId * 4
// Transform IDs: 0=none, 1=rot90, 2=rot180, 3=rot270, 4=flipH, 5=flipH+rot90, 6=flipH+rot180, 7=flipH+rot270
function encodeTexInfo(x: number, z: number, direction: number): number {
  const textureId = hashCell(x, z, direction) % 4;
  const transformId = hashCellTransform(x, z, direction) % 8;
  return textureId + transformId * 4;
}

// Add a quad to the vertex array (2 triangles = 6 vertices)
// Each vertex has position (3) + normal (3) + uv (2) + texIndex (1) = 9 floats
function addQuadWithUV(
  vertices: number[],
  p1: number[], p2: number[], p3: number[], p4: number[],
  normal: number[],
  uv1: number[], uv2: number[], uv3: number[], uv4: number[],
  texIndex: number
): void {
  // Triangle 1: p1, p2, p3
  vertices.push(...p1, ...normal, ...uv1, texIndex);
  vertices.push(...p2, ...normal, ...uv2, texIndex);
  vertices.push(...p3, ...normal, ...uv3, texIndex);

  // Triangle 2: p1, p3, p4
  vertices.push(...p1, ...normal, ...uv1, texIndex);
  vertices.push(...p3, ...normal, ...uv3, texIndex);
  vertices.push(...p4, ...normal, ...uv4, texIndex);
}

export interface WallGeometry {
  north: Geometry;
  south: Geometry;
  east: Geometry;
  west: Geometry;
}

export function generateWallGeometry(dungeon: Dungeon): WallGeometry {
  const northVerts: number[] = [];
  const southVerts: number[] = [];
  const eastVerts: number[] = [];
  const westVerts: number[] = [];
  const cellSize = dungeon.cellSize;

  for (let z = 0; z < dungeon.height; z++) {
    for (let x = 0; x < dungeon.width; x++) {
      const cell = dungeon.getCell(x, z);
      if (!cell || cell.type !== CellType.SOLID) continue;

      const worldX = x * cellSize;
      const worldZ = z * cellSize;

      // Check each adjacent cell - if it's open, add a wall face
      // Vertices must be CCW when viewed from the open cell

      // North face (z-1) - visible from lower Z
      if (dungeon.isWalkable(x, z - 1)) {
        const texInfo = encodeTexInfo(x, z, 0);
        addQuadWithUV(northVerts,
          [worldX, 0, worldZ],
          [worldX, WALL_HEIGHT, worldZ],
          [worldX + cellSize, WALL_HEIGHT, worldZ],
          [worldX + cellSize, 0, worldZ],
          [0, 0, -1],
          [0, 1], [0, 0], [1, 0], [1, 1],
          texInfo
        );
      }

      // South face (z+1) - visible from higher Z
      if (dungeon.isWalkable(x, z + 1)) {
        const texInfo = encodeTexInfo(x, z, 1);
        addQuadWithUV(southVerts,
          [worldX + cellSize, 0, worldZ + cellSize],
          [worldX + cellSize, WALL_HEIGHT, worldZ + cellSize],
          [worldX, WALL_HEIGHT, worldZ + cellSize],
          [worldX, 0, worldZ + cellSize],
          [0, 0, 1],
          [0, 1], [0, 0], [1, 0], [1, 1],
          texInfo
        );
      }

      // West face (x-1) - visible from lower X
      if (dungeon.isWalkable(x - 1, z)) {
        const texInfo = encodeTexInfo(x, z, 2);
        addQuadWithUV(westVerts,
          [worldX, 0, worldZ],
          [worldX, 0, worldZ + cellSize],
          [worldX, WALL_HEIGHT, worldZ + cellSize],
          [worldX, WALL_HEIGHT, worldZ],
          [-1, 0, 0],
          [1, 1], [0, 1], [0, 0], [1, 0],
          texInfo
        );
      }

      // East face (x+1) - visible from higher X
      if (dungeon.isWalkable(x + 1, z)) {
        const texInfo = encodeTexInfo(x, z, 3);
        addQuadWithUV(eastVerts,
          [worldX + cellSize, 0, worldZ + cellSize],
          [worldX + cellSize, 0, worldZ],
          [worldX + cellSize, WALL_HEIGHT, worldZ],
          [worldX + cellSize, WALL_HEIGHT, worldZ + cellSize],
          [1, 0, 0],
          [0, 1], [1, 1], [1, 0], [0, 0],
          texInfo
        );
      }
    }
  }

  return {
    north: { vertices: new Float32Array(northVerts), vertexCount: northVerts.length / 9 },
    south: { vertices: new Float32Array(southVerts), vertexCount: southVerts.length / 9 },
    east: { vertices: new Float32Array(eastVerts), vertexCount: eastVerts.length / 9 },
    west: { vertices: new Float32Array(westVerts), vertexCount: westVerts.length / 9 }
  };
}

export function generateFloorGeometry(dungeon: Dungeon): Geometry {
  const vertices: number[] = [];
  const cellSize = dungeon.cellSize;
  const texIndex = 0; // Floor texture index (not used for floor/ceiling, but kept for consistency)

  for (let z = 0; z < dungeon.height; z++) {
    for (let x = 0; x < dungeon.width; x++) {
      if (!dungeon.isWalkable(x, z)) continue;

      const worldX = x * cellSize;
      const worldZ = z * cellSize;

      // Floor quad (y = 0)
      addQuadWithUV(vertices,
        [worldX, 0, worldZ + cellSize],
        [worldX + cellSize, 0, worldZ + cellSize],
        [worldX + cellSize, 0, worldZ],
        [worldX, 0, worldZ],
        [0, 1, 0],  // Normal points up
        [0, 1], [1, 1], [1, 0], [0, 0],
        texIndex
      );
    }
  }

  return {
    vertices: new Float32Array(vertices),
    vertexCount: vertices.length / 9
  };
}

export function generateCeilingGeometry(dungeon: Dungeon): Geometry {
  const vertices: number[] = [];
  const cellSize = dungeon.cellSize;
  const texIndex = 0; // Ceiling texture index (not used for floor/ceiling, but kept for consistency)

  for (let z = 0; z < dungeon.height; z++) {
    for (let x = 0; x < dungeon.width; x++) {
      if (!dungeon.isWalkable(x, z)) continue;

      const worldX = x * cellSize;
      const worldZ = z * cellSize;

      // Ceiling quad (y = WALL_HEIGHT)
      addQuadWithUV(vertices,
        [worldX, WALL_HEIGHT, worldZ],
        [worldX + cellSize, WALL_HEIGHT, worldZ],
        [worldX + cellSize, WALL_HEIGHT, worldZ + cellSize],
        [worldX, WALL_HEIGHT, worldZ + cellSize],
        [0, -1, 0],  // Normal points down
        [0, 0], [1, 0], [1, 1], [0, 1],
        texIndex
      );
    }
  }

  return {
    vertices: new Float32Array(vertices),
    vertexCount: vertices.length / 9
  };
}

export function createVAO(gl: WebGL2RenderingContext, geometry: Geometry): WebGLVertexArrayObject | null {
  const vao = gl.createVertexArray();
  if (!vao) return null;

  gl.bindVertexArray(vao);

  const vbo = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, geometry.vertices, gl.STATIC_DRAW);

  const stride = 9 * 4; // 9 floats per vertex

  // Position attribute (location 0)
  gl.enableVertexAttribArray(0);
  gl.vertexAttribPointer(0, 3, gl.FLOAT, false, stride, 0);

  // Normal attribute (location 1)
  gl.enableVertexAttribArray(1);
  gl.vertexAttribPointer(1, 3, gl.FLOAT, false, stride, 3 * 4);

  // UV attribute (location 2)
  gl.enableVertexAttribArray(2);
  gl.vertexAttribPointer(2, 2, gl.FLOAT, false, stride, 6 * 4);

  // Texture index attribute (location 3)
  gl.enableVertexAttribArray(3);
  gl.vertexAttribPointer(3, 1, gl.FLOAT, false, stride, 8 * 4);

  gl.bindVertexArray(null);

  return vao;
}
